// 2026-04-24: Helper render rich text content (HTML từ RichTextEditor / admin nhập / AI output)
// NV xem phải thấy format giống admin nhập.
//
// 2026-04-24 SEC (Phase 1): Hardening DOMPurify — chuyển từ USE_PROFILES mềm sang
// strict ALLOWED_TAGS/ATTR + FORBID_ATTR cho tất cả event handlers + style inline.
// Hook thêm rel="noopener noreferrer" cho <a target="_blank"> chống tabnabbing.
// Áp cho mọi dangerouslySetInnerHTML trong app (BookmarksPage, NoiQuyPage, FlashcardPage,
// RulesReaderPage, ExamPage, AskPage, ...).
//
// Logic:
//   - Nếu content bắt đầu với HTML tag → sanitize + render trực tiếp
//   - Nếu plain text (data cũ không qua editor) → wrap <p> + convert newline
//   - Nếu empty → return ''

import DOMPurify from 'dompurify';
import { marked } from 'marked';

const HTML_TAG_RE = /^<(p|h[1-6]|ul|ol|blockquote|pre|img|div|figure|hr|strong|em|span|a|table|section)/i;

// M60 (2026-04-25): Detect markdown patterns — content có ít nhất 1 marker
// → parse qua marked. Cover cases: AI generate câu trả lời, admin paste md thô.
const MARKDOWN_HINTS = [
  /^#{1,6}\s+/m,         // # heading, ## heading
  /^\s*[-*+]\s+/m,       // - bullet, * bullet, + bullet
  /^\s*\d+\.\s+/m,       // 1. numbered
  /\*\*[^*\n]+\*\*/,     // **bold**
  /(?<!\*)\*[^*\n]+\*(?!\*)/,  // *italic*
  /^>\s+/m,              // > blockquote
  /^---+$/m,             // --- horizontal rule
  /\[[^\]]+\]\([^)]+\)/, // [link](url)
  /`[^`\n]+`/,           // `inline code`
  /^```/m,               // ``` code block
];
export function looksLikeMarkdown(text) {
  return MARKDOWN_HINTS.some((re) => re.test(text));
}

/**
 * M62 (2026-04-25): Convert content thành HTML cho TipTap editor (WYSIWYG).
 *
 * - Nếu là HTML rồi → return nguyên (TipTap parse HTML đẹp)
 * - Nếu là markdown → marked.parse → HTML (TipTap render với heading/bold/list)
 * - Plain text → wrap <p> + escape + nl2br
 *
 * Dùng khi mở modal "Sửa câu hỏi" với content cũ là markdown (do AI generate).
 * Sau khi admin save, content được lưu lại dạng HTML → render list cũng đẹp.
 */
export function toHtmlForEditor(content) {
  if (!content) return '';
  const trimmed = String(content).trim();
  if (HTML_TAG_RE.test(trimmed)) return trimmed;
  if (looksLikeMarkdown(trimmed)) {
    try { return marked.parse(trimmed); } catch { /* fallthrough */ }
  }
  return `<p>${escapeHtml(trimmed).replace(/\n/g, '<br>')}</p>`;
}

// Cấu hình marked: GFM (table, strike) + breaks (single \n → <br>)
marked.setOptions({ gfm: true, breaks: true });

// Strict allowlist — tất cả tag format thường dùng + table + rich content.
// KHÔNG cho: script, style, iframe, object, embed, form, input, button, textarea,
// select, link, meta, base, svg (svg có thể chứa JS qua foreignObject), math.
const ALLOWED_TAGS = [
  'p', 'br', 'hr', 'div', 'section', 'span',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'strong', 'b', 'em', 'i', 'u', 's', 'mark', 'sub', 'sup',
  'ul', 'ol', 'li',
  'blockquote', 'pre', 'code',
  'a', 'img', 'figure', 'figcaption',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption', 'colgroup', 'col',
];

// Attributes whitelist — `style` cho phép NHƯNG hook filter chỉ giữ properties
// an toàn (M88, 2026-04-26: để giữ format Word import — color, background, bold).
const ALLOWED_ATTR = [
  'href', 'src', 'alt', 'title',
  'target', 'rel',
  'class', 'id',
  'width', 'height',
  'colspan', 'rowspan',
  'align', 'valign',
  'lang', 'dir',
  'style',           // M88 — hook bên dưới strip CSS không an toàn
  'bgcolor',         // mammoth Word export cho table cells
];

// Extra defense-in-depth — explicitly forbid các attr nhạy cảm cho dù DOMPurify tự strip.
const FORBID_ATTR = [
  'onerror', 'onload', 'onclick', 'onmouseover', 'onmouseout',
  'onfocus', 'onblur', 'onchange', 'onsubmit', 'onkeydown', 'onkeyup', 'onkeypress',
  'onanimationstart', 'onanimationend', 'ontransitionend',
  'formaction', 'action',
  'srcset', 'ping', 'data-*',  // data-* để tránh inject data-on-* workaround
];

// M88: CSS properties an toàn cho phép trong style="..." — đủ cho Word import
// (color, background, bold/font-weight, gạch chân, căn lề, list style).
const ALLOWED_CSS_PROPS = new Set([
  'color',
  'background-color',
  'background',           // background shorthand (chỉ color, không URL)
  'font-weight',
  'font-style',
  'text-decoration',
  'text-decoration-line',
  'text-align',
  'text-indent',
  'list-style-type',
  'font-size',            // mammoth có thể export; cap qua giới hạn dưới
  'line-height',
]);
const CSS_VALUE_BLACKLIST = /(expression\(|javascript:|vbscript:|data:[^;,]*[\(\)]|url\s*\()/i;

function sanitizeStyleAttr(value) {
  if (!value || typeof value !== 'string') return '';
  return value.split(';')
    .map(decl => decl.trim())
    .filter(Boolean)
    .map(decl => {
      const idx = decl.indexOf(':');
      if (idx <= 0) return null;
      const prop = decl.slice(0, idx).trim().toLowerCase();
      const val  = decl.slice(idx + 1).trim();
      if (!ALLOWED_CSS_PROPS.has(prop)) return null;
      if (CSS_VALUE_BLACKLIST.test(val)) return null;
      // background shorthand chỉ giữ nếu thuần color (no url/expression)
      return `${prop}: ${val}`;
    })
    .filter(Boolean)
    .join('; ');
}

// Hook: tự thêm rel="noopener noreferrer" cho <a target="_blank">.
// Chạy 1 lần khi module load — DOMPurify hook global cho toàn app.
let hooksInstalled = false;
function installHooks() {
  if (hooksInstalled) return;
  hooksInstalled = true;
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    // External link hardening
    if (node.tagName === 'A') {
      // Nếu target="_blank" → ép rel="noopener noreferrer"
      if (node.getAttribute('target') === '_blank') {
        node.setAttribute('rel', 'noopener noreferrer');
      }
      // Chặn javascript: / vbscript: / data: URLs (DOMPurify đã chặn nhưng double-check)
      const href = node.getAttribute('href') || '';
      if (/^\s*(javascript|vbscript|data):/i.test(href)) {
        node.removeAttribute('href');
      }
    }
    // IMG: chặn non-http(s) và non-relative
    if (node.tagName === 'IMG') {
      const src = node.getAttribute('src') || '';
      // Cho phép: http(s)://, /uploads/..., /static/..., ./, data:image/
      const isAllowed =
        /^https?:\/\//i.test(src) ||
        /^\/(uploads|static|api\/uploads)\//i.test(src) ||
        /^\.\.?\//.test(src) ||
        /^data:image\/(png|jpe?g|gif|webp|svg\+xml);base64,/i.test(src);
      if (!isAllowed) node.removeAttribute('src');
    }
    // M88 (2026-04-26): filter style attr — chỉ giữ CSS properties an toàn
    // (color, background-color, font-weight, ...). Cho phép giữ format từ
    // Word import (mammoth) mà không mở rộng XSS surface.
    if (node.hasAttribute && node.hasAttribute('style')) {
      const cleaned = sanitizeStyleAttr(node.getAttribute('style'));
      if (cleaned) node.setAttribute('style', cleaned);
      else node.removeAttribute('style');
    }
  });
}
installHooks();

/**
 * Render rich text với sanitization strict. An toàn cho XSS.
 * @param {string} content - HTML từ TipTap / admin / AI output / plain text cũ
 * @returns {string} sanitized HTML
 */
export function renderRichText(content) {
  if (!content) return '';
  const trimmed = String(content).trim();
  const isHtml = HTML_TAG_RE.test(trimmed);

  let raw;
  if (isHtml) {
    // Đã là HTML (từ TipTap / mammoth import) — chỉ sanitize
    raw = trimmed;
  } else if (looksLikeMarkdown(trimmed)) {
    // M60 (2026-04-25): markdown detected (AI generate, paste raw md...) → parse qua marked
    try {
      raw = marked.parse(trimmed);
    } catch {
      // Fallback nếu marked throw (markdown lạ) → escape + wrap <p>
      raw = `<p>${escapeHtml(trimmed).replace(/\n/g, '<br>')}</p>`;
    }
  } else {
    // Plain text — escape + wrap để hiển thị nguyên bản
    raw = `<p>${escapeHtml(trimmed).replace(/\n/g, '<br>')}</p>`;
  }

  return DOMPurify.sanitize(raw, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    FORBID_ATTR,
    ALLOW_DATA_ATTR: false,
    ALLOW_UNKNOWN_PROTOCOLS: false,
    USE_PROFILES: false,
  });
}

/**
 * Helper tiện dụng cho JSX: `<div dangerouslySetInnerHTML={safeRichHtml(content)} />`
 * Gom pattern `{ __html: renderRichText(x) }` lặp trong nhiều page.
 */
export function safeRichHtml(content) {
  return { __html: renderRichText(content) };
}

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Strip HTML tags → plain text (dùng cho search, preview ngắn).
 */
export function stripHtml(content) {
  if (!content) return '';
  return String(content).replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
}

/**
 * M74 (2026-04-25): Plain-text preview cho list/collapsed view.
 *
 * Khác stripHtml: cũng strip cả markdown markers (** __ ## - * etc).
 * Lý do: AI generate output markdown — nếu chỉ stripHtml thì preview hiện
 * raw `**bold**`, `## heading`. Helper này convert md → HTML → strip → plain.
 *
 * Dùng cho: AdminRulesModulesPage QuestionEditor preview + bất kỳ chỗ nào
 * cần text-only preview của content có thể là HTML / markdown / plain.
 */
export function toPlainPreview(content) {
  if (!content) return '';
  const trimmed = String(content).trim();
  // Nếu là HTML → strip thẳng
  if (HTML_TAG_RE.test(trimmed)) {
    return stripHtml(trimmed);
  }
  // Nếu là markdown → parse sang HTML rồi strip
  if (looksLikeMarkdown(trimmed)) {
    try {
      const html = marked.parse(trimmed);
      return stripHtml(html);
    } catch { /* fallthrough */ }
  }
  // Plain text — chỉ collapse whitespace
  return trimmed.replace(/\s+/g, ' ');
}
